<template>
  <div class="checkout-container">
    <PurchaseReviewComponent
      :invoice="invoice"
      :invoice-payments="invoicePayments"
      :total="total"
      :is-thinking="isThinking || isLoading"
      :use-arta="useArta"
      :non-shipping-invoice-items="nonShippingInvoiceItems"
      :selected-shipping-option="selectedShippingOption"
      :selected-pick-up="selectedPickUp"
    />
    <PaymentCollectionComponent
      v-if="invoice"
      ref="paymentCollectionComponent"
      :loading-exemption-status="loadingExemptionStatus"
      :credit-card-payment-restricted="creditCardPaymentRestricted"
      :invoice="invoice"
      :invoice-payments="invoicePayments"
      :total="total"
      :radio-picked="radioPicked"
      :check-restricted="checkRestricted"
      :zelle-restricted="zelleRestricted"
      :is-thinking="isThinking"
      :exit="exit"
      :bread-crumbs="breadCrumbs"
      :is-staff-view="isStaffView"
      :customer-name="customerName"
      :toggle-upload-certificate-form="toggleUploadCertificateForm"
      :update-loading="updateLoading"
      :show-error="showError"
      :update-payment-type="updatePaymentType"
      :show-loading-message="showLoadingMessage"
      :show-review-payment="showReviewPayment"
    />
    <LoadingOverlayComponent
      v-if="showLoadingOverlay || showLoading"
      :title="loadingTitle"
    />
    <AlertComponent
      v-show="error.show"
      :title="error.title"
      :message="error.message"
      @close="closeError()"
    />
    <ProcessPaymentOverlayComponent
      v-if="showPaymentProcessing"
      :in-progress="paymentInProgress"
      :payment-type="paymentType"
      :is-pickup="selectedPickUp"
      :use-arta="useArta"
      @clicked="exitToInvoices"
    />
    <AlertComponent
      v-if="showAlert"
      :title="alertTitle"
      :message="alertMessage"
      :secondary-action="alertSecondaryAction"
      @close="exit()"
    />

    <ReviewPaymentComponent 
      v-if="showReviewPaymentModal"
      :for-partial-payment="forPartialPayment"
      :invoice="invoice"
      :partial-payment-amount="partialPaymentAmount"
      :use-arta="useArta"
      :total="total"
      :selected-shipping-option="selectedShippingOption"
      :selected-pick-up="selectedPickUp"
      @close="exitReview()"
      @submit="submitPayment()"
    />

    <div v-if="!loadingExemptionStatus && !loadingInvoice">
      <CertCaptureModalComponent
        :show="uploadingCertificate"
        :on-cancel="toggleUploadCertificateForm"
        :on-cert-success="onCertSuccess"
        :destination="taxDestination"
      />
    </div>
  </div>
</template>

<script>
import AlertComponent from '@/components/Shared/AlertComponent.vue'
import ProcessPaymentOverlayComponent from '@/components/Shared/ProcessPaymentOverlayComponent.vue'
import PaymentCollectionComponent from './PaymentInfo/PaymentCollectionComponent.vue'
import PurchaseReviewComponent from './PaymentInfo/PurchaseReviewComponent.vue'
import apiMixin from '@/mixins/apiMixin.js'
import LoadingOverlayComponent from '@/components/Shared/LoadingOverlayComponent.vue'
import LiveMode from '../LiveMode.vue'
import InvoiceValidateMixin from '@/mixins/InvoiceValidateMixin.js'
import PaymentInfoMixin from '@/mixins/PaymentInfoMixin.js'
import CertCaptureModalComponent from './CertCaptureModalComponent.vue'
import ReviewPaymentComponent from '@/components/Checkout/Shared/ReviewPaymentComponent.vue'

/**
 * The Payment Info Checkout component.
 * @displayName Payment Info Component
 */
export default {
  name: 'PaymentInfoComponent',
  components: {
    ProcessPaymentOverlayComponent,
    AlertComponent,
    LoadingOverlayComponent,
    LiveMode,
    CertCaptureModalComponent,
    PurchaseReviewComponent,
    PaymentCollectionComponent,
    ReviewPaymentComponent
  },
  mixins: [apiMixin, InvoiceValidateMixin, PaymentInfoMixin],
  data () {
    return {
      error: {
        show: false,
        fatal: false,
        title: '',
        message: ''
      },
      showAlert: false,
      isLoading: false,
      braintreeIsThinking: false,
      showPaymentProcessing: false,
      paymentInProgress: true,
      radioPicked: null,
      uploadingCertificate: false,
      loadingTitle: 'Preparing your invoice',
      creditCardPaymentRestricted: true,
      checkRestricted: true,
      zelleRestricted: true,
      useArta: true,
      paymentType: '',
      taxReloadTries: 5,
      taxReloadInProgress: false,
      certificateUploadSuccess: false,
      loadingExemptionStatus: false,
      updatingExemptionCertificate: false,
      exemptionStatus: null,
      loadingInvoice: false,
      warningMessageShown: false,
      alertTitle: '',
      alertMessage:
        'If you have any questions, please contact us here: info@lelandlittle.com',
      alertSecondaryAction: null,
      streetAddress: '',
      showLoading: false,
      showReviewPaymentModal: false,
      forPartialPayment: false,
      partialPaymentAmount: 0
    }
  },
  async mounted () {
    // Need to recalulate the tax on the current invoice
    // Only if on payment screen
    if (this.$router.currentRoute.path.includes('payment')) {
      // Calculate tax on this invoice
      this.isLoading = true
      this.loadingInvoice = true
      this.loadingExemptionStatus = true
      await this.$store.dispatch('getCustomerInvoice', {
        invoiceId: this.currentInvoiceId,
        recalculateTax: true
      })
      // Hello. We must also get the shipping orders again.
      await this.$store.dispatch('getAllCustomerShippingOrders')

      // If we don't have any shipping orders and we are not fully paid, AND we can use arta, we should go to start
      if (this.invoice && this.invoice.shipping_order_invoice === false && this.invoice.invoice_balance > 0 && this.canUseArta(this.invoice) === true) {
        this.$router.push({ name: 'auctionItems' })
        return
      }
      this.loadingInvoice = false
      // Now we can get the exemption status, if we do before we have recalculated the tax,
      // it may not be correct.
      await this.$store.dispatch('getInvoiceExemptionStatus', {
        invoiceId: this.currentInvoiceId
      })

      this.validateInvoice(this.invoice)
      this.loadingExemptionStatus = false
      this.isLoading = false
    }
  },
  methods: {
    exitReview(){
      this.showReviewPaymentModal = false
    },
    submitPayment(){
      this.showReviewPaymentModal = false
      this.$refs.paymentCollectionComponent.submitPayment()
    },
    /**
     * Toggle loading overlay
     *
     * @param {bool} setShowPaymentProcessing
     * @param {bool} setPaymentInProgress
     * @returns {void}
     */
    updateLoading (setShowPaymentProcessing, setPaymentInProgress) {
      this.showPaymentProcessing = setShowPaymentProcessing
      this.paymentInProgress = setPaymentInProgress
    },
    showLoadingMessage(show, message){
      if(!show){
        this.showLoading = false
        this.loadingTitle = 'Preparing your invoice'
      } else {
        this.showLoading = true
        this.loadingTitle = message
      }
    },
    showReviewPayment(show, partialPayment, partialPaymentAmount){
      this.showReviewPaymentModal = show
      this.forPartialPayment = partialPayment
      this.partialPaymentAmount = partialPaymentAmount
    },
    /**
     * Toggle error alert
     * @param {bool} show
     * @param {string} title
     * @param {string} message
     */
    showError (show, title, message) {
      this.error.show = show
      this.error.title = title
      this.error.message = message
    },
    /**
     * Set the payment type
     * @param {string} type
     */
    updatePaymentType (type) {
      this.paymentType = type
    }
  }
}
</script>

<style scoped></style>
